import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchFromPaymentsAPI, parseBackendDate } from '../services/common.js';
import Icon from './Icon';

// ---------------------------------------------------------------------------
// NotificationsTab — a chronological (new → old) feed of payments that did NOT
// complete, for a practice. Three kinds, all derived client-side from the same
// /api/payments/invoices payload the Invoices / Reconciliation tabs consume:
//
// 1. DECLINES — the payin.failed webhook stamps a ``payment_failed`` entry onto
//    the invoice ``activityLog`` (see handle_payin_webhook in backend/server.py):
//      { action:'payment_failed', timestamp,
//        details:{ payinId, refusalCode, refusalDescription, cardBrand, cardLast4 } }
//    A single invoice can appear multiple times (a card retried and declined
//    again). These almost always happen at authorization, BEFORE processing.
//
// 2. PROCESSING → DIDN'T COMPLETE — the case that actually alarms an office
//    manager: the payment showed ⏳ Processing with NO error (the patient got a
//    "payment is being processed" SMS, staff got the "Payments processing"
//    admin email), and then hours or days later it never settled. Verified
//    against prod (Sep 2026): this does NOT surface as ``payin.failed`` — the
//    payin instead ends in ``payin.canceled`` (a Rainforest-side void) and, less
//    commonly, could end in ``payin.returned`` (ACH). Because a void/return
//    writes NO ``payment_failed`` activity entry, kind #1 misses these entirely
//    (that's why the old "payin.failed only" logic reported 0). So we read the
//    raw webhook timeline: a PAYIN that emitted ``payin.authorized`` /
//    ``payin.processing`` and then ended in ``payin.canceled`` / ``payin.failed``
//    / ``payin.returned`` WITHOUT ever emitting ``payin.succeeded`` — and whose
//    INVOICE didn't ultimately get paid on some other attempt. Real examples:
//    Peter Pease $3,354.97 and Charles Butros $100 (~5 h in processing, then
//    voided); DALIA LEVY $36 at Growney1 (the manual_fix_payin_canceled case).
//    These are flagged with a red "⏳→✗ Processing <gap> then voided/failed"
//    badge and isolated by the "Processing → didn't complete" filter.
//
// 3. LEDGER WRITEBACK FAILED — the payment settled fine with the patient, but
//    we couldn't post it to the practice's PMS ledger (Dentrix via Zatanna).
//    The backend stamps ``invoice.ledgerPost`` with status 'failed' (the live
//    write threw — dead on-prem agent, contract mismatch, transport error) or
//    'skipped' (a prerequisite was missing so we couldn't even attempt: no
//    payment type / provider / amount / patient). Both mean "money in, ledger
//    out of sync" — staff need to post it manually or fix config and retry.
//    Flagged with an amber "PMS ledger not updated" badge + a "Ledger
//    writeback" filter. Only appears once writeback is enabled for the
//    practice, since the stamp is only written on the autopost path.
// ---------------------------------------------------------------------------

// Friendly text for a ledgerPost stamp that didn't post. 'write_error' uses
// the captured error string; the rest are missing-prerequisite skip reasons.
const LEDGER_FAIL_REASON = {
  write_error: 'PMS ledger write failed',
  no_payment_type_id:
    'No matching PMS payment type — set a card mapping or default in Settings',
  no_provider_id: 'Could not determine a provider to credit',
  no_patient_id: 'Invoice has no linked patient',
  no_amount: 'Invoice has no amount',
  no_claim: 'Insurance invoice has no linked claim — post it in the PMS',
  claim_not_found: 'Linked claim is no longer in the PMS — post it in the PMS',
  allocations_incomplete:
    'Some patients on this insurance payment did not post — open the invoice to see which',
  estimate_mismatch:
    "Insurance amount differs from the claim's estimate — post it in the PMS from the EOB",
  claim_already_paid: 'Claim already has an insurance payment — post it in the PMS',
  no_claim_lines: 'No procedures left to pay on this claim — post it in the PMS',
  no_insurance_payment_type: 'No insurance payment type found in the PMS — post it in the PMS',
  payment_unverified:
    "Couldn't confirm the payment with the processor — not posted; post it in the PMS once it settles",
};

// A ledgerPost stamp counts as a notification when the write didn't land.
const LEDGER_FAIL_STATUS = new Set(['failed', 'skipped']);

// Terminal payin states that mean "did not settle".
const TERMINAL_NONSUCCESS = new Set(['payin.canceled', 'payin.failed', 'payin.returned']);
const END_REASON = {
  'payin.canceled': 'Voided after processing (never settled)',
  'payin.returned': 'Returned after processing (ACH)',
  'payin.failed': 'Failed after processing',
};

const payinIdOf = (hook) => (hook && hook.data && hook.data.data && hook.data.data.payin_id) || 'na';

// Scan an invoice's webhook timeline for payins that reached processing and
// then ended in a non-success terminal state without ever succeeding. Returns
// one descriptor per such payin: { payinId, endEvent, procMs, endMs, gapMs }.
const processingThenIncomplete = (invoice) => {
  const hooks = Array.isArray(invoice.payinWebhooks) ? invoice.payinWebhooks : [];
  const byPayin = new Map();
  hooks.forEach((h) => {
    const pid = payinIdOf(h);
    if (!byPayin.has(pid)) byPayin.set(pid, []);
    byPayin.get(pid).push(h);
  });

  const out = [];
  byPayin.forEach((evs, pid) => {
    const sorted = evs
      .slice()
      .sort((a, b) => ((a.received_at || '') < (b.received_at || '') ? -1 : 1));
    let procMs = null;
    let succeeded = false;
    let endEvent = null;
    let endMs = null;
    sorted.forEach((h) => {
      const et = h && h.event_type;
      const d = parseBackendDate(h.received_at);
      const ms = d ? d.getTime() : null;
      if ((et === 'payin.processing' || et === 'payin.authorized') && procMs == null) procMs = ms;
      if (et === 'payin.succeeded') succeeded = true;
      // First terminal non-success that lands after the payin reached processing.
      if (TERMINAL_NONSUCCESS.has(et) && procMs != null && !succeeded && endEvent == null) {
        endEvent = et;
        endMs = ms;
      }
    });
    if (procMs != null && endEvent != null && !succeeded) {
      out.push({ payinId: pid, endEvent, procMs, endMs, gapMs: endMs != null && procMs != null ? endMs - procMs : null });
    }
  });
  return out;
};

const usd = (dollars) => {
  const n = Number(dollars) || 0;
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const formatDateTime = (date) => {
  if (!date) return '—';
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

// Build the unified feed of non-completing payments across all invoices, most
// recent first. Merges (1) card declines from ``payment_failed`` activity and
// (2) "processing → didn't complete" payins read off the webhook timeline.
const extractFailures = (invoices) => {
  const rows = [];
  (invoices || []).forEach((inv) => {
    const status = (inv.status || '').toLowerCase();
    const isPaid = status === 'paid';
    const card = inv.paymentCard || {};

    // (1) Declines — one row per payment_failed activity entry.
    const log = Array.isArray(inv.activityLog) ? inv.activityLog : [];
    log.forEach((entry, idx) => {
      if (!entry || entry.action !== 'payment_failed') return;
      const details = entry.details || {};
      rows.push({
        key: `decline:${inv.id}:${details.payinId || 'na'}:${idx}`,
        kind: 'decline',
        invoiceId: inv.id,
        patientName: inv.patientName || '—',
        amount: inv.total,
        payinId: details.payinId || '',
        reason: details.refusalDescription || 'Payment was declined',
        cardBrand: details.cardBrand || '',
        cardLast4: details.cardLast4 || '',
        date: parseBackendDate(entry.timestamp),
        invoiceStatus: status,
        processingThenFailed: false,
        processingGapMs: null,
      });
    });

    // (2) Processing → didn't complete — one row per offending payin. Excluded
    // when the invoice ultimately got paid (another attempt succeeded), since a
    // void that was later re-collected isn't the problem.
    if (!isPaid) {
      processingThenIncomplete(inv).forEach((p) => {
        rows.push({
          key: `stalled:${inv.id}:${p.payinId}`,
          kind: 'processing_incomplete',
          invoiceId: inv.id,
          patientName: inv.patientName || '—',
          amount: inv.total,
          payinId: p.payinId === 'na' ? '' : p.payinId,
          reason: END_REASON[p.endEvent] || 'Did not complete after processing',
          cardBrand: card.brand_desc || card.brand || '',
          cardLast4: card.last_4 || '',
          date: p.endMs != null ? new Date(p.endMs) : null,
          invoiceStatus: status,
          processingThenFailed: true,
          processingGapMs: p.gapMs,
        });
      });
    }

    // (2b) Posted to the PMS, then voided / failed / returned / refunded:
    // the ledger entry has to be reversed by hand.
    const reversal = (inv.ledgerPost || {}).reversal;
    if (reversal && reversal.status === 'needed') {
      rows.push({
        key: `reversal:${inv.id}`,
        kind: 'ledger_reversal',
        invoiceId: inv.id,
        patientName: inv.patientName || '—',
        amount: reversal.amount != null ? reversal.amount : inv.total,
        payinId: reversal.payinId || '',
        reason: `${reversal.reason || 'Payment reversed after it was posted'} — reverse it in the PMS`,
        cardBrand: card.brand_desc || card.brand || '',
        cardLast4: card.last_4 || '',
        date: parseBackendDate(reversal.flaggedAt),
        invoiceStatus: status,
        processingThenFailed: false,
        processingGapMs: null,
        ledgerFailed: true,
      });
    }

    // (3) Ledger writeback failed/skipped — the payment succeeded with the
    // patient but didn't make it into the PMS ledger. One row per invoice
    // (the stamp reflects the latest attempt).
    const lp = inv.ledgerPost || {};
    if (LEDGER_FAIL_STATUS.has(lp.status)) {
      rows.push({
        key: `ledger:${inv.id}`,
        kind: 'ledger_failed',
        invoiceId: inv.id,
        patientName: inv.patientName || '—',
        amount: inv.total,
        payinId: '',
        reason:
          (lp.status === 'failed' && lp.error)
            ? `PMS ledger write failed: ${lp.error}`
            : (LEDGER_FAIL_REASON[lp.reason] || 'PMS ledger not updated'),
        cardBrand: card.brand_desc || card.brand || '',
        cardLast4: card.last_4 || '',
        date: parseBackendDate(lp.failedAt || lp.stampedAt),
        invoiceStatus: status,
        processingThenFailed: false,
        processingGapMs: null,
        ledgerFailed: true,
      });
    }
  });

  rows.sort((a, b) => {
    const ta = a.date ? a.date.getTime() : 0;
    const tb = b.date ? b.date.getTime() : 0;
    return tb - ta; // new → old
  });
  return rows;
};

// Human "3h", "2d", "45m" for the processing→failure window.
const formatGap = (ms) => {
  if (ms == null) return '';
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
};

const NotificationsTab = ({ practiceId }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // 'all' | 'processing' — 'processing' isolates the "showed Processing with no
  // error, then failed and never settled" subset (see extractFailures).
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    if (!practiceId) return;
    setLoading(true);
    setError('');
    try {
      const data = await fetchFromPaymentsAPI(
        `/api/payments/invoices?practiceId=${practiceId}`
      );
      if (data && data.success) {
        setInvoices(data.invoices || []);
      } else {
        setError(data?.error || 'Failed to load notifications.');
        setInvoices([]);
      }
    } catch (err) {
      console.error('Error loading notifications:', err);
      setError('Failed to load notifications. Please check your connection.');
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [practiceId]);

  useEffect(() => {
    load();
  }, [load]);

  const failures = useMemo(() => extractFailures(invoices), [invoices]);
  const processingFailedCount = useMemo(
    () => failures.filter((f) => f.processingThenFailed).length,
    [failures]
  );
  const ledgerFailedCount = useMemo(
    () => failures.filter((f) => f.ledgerFailed).length,
    [failures]
  );
  const visibleFailures = useMemo(() => {
    if (filter === 'processing') return failures.filter((f) => f.processingThenFailed);
    if (filter === 'ledger') return failures.filter((f) => f.ledgerFailed);
    return failures;
  }, [failures, filter]);

  const thStyle = {
    textAlign: 'left',
    padding: '10px 12px',
    fontSize: '0.78rem',
    fontWeight: 700,
    color: '#3c4540',
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    borderBottom: '2px solid #e3e7e4',
    whiteSpace: 'nowrap',
  };
  const thRight = { ...thStyle, textAlign: 'right' };
  const tdStyle = {
    padding: '10px 12px',
    fontSize: '0.9rem',
    color: '#1a1d1b',
    borderBottom: '1px solid #eef0ee',
    verticalAlign: 'top',
  };
  const tdRight = { ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
        }}
      >
        <p style={{ margin: 0, color: '#6b716d', fontSize: '0.95rem' }}>
          Payments that didn't complete, most recent first.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {/* Segmented filter (same control as the Invoices tab): everything
              vs the "processing then didn't complete" / ledger subsets. */}
          <div className="pm-segmented" role="group" aria-label="Notification filter">
            {[
              { id: 'all', label: 'All', count: failures.length },
              { id: 'processing', label: "Processing → didn't complete", count: processingFailedCount },
              { id: 'ledger', label: 'Ledger writeback', count: ledgerFailedCount },
            ].map((opt) => (
              <button
                key={opt.id}
                type="button"
                className="pm-segment"
                aria-pressed={filter === opt.id}
                onClick={() => setFilter(opt.id)}
              >
                {opt.label}
                <span className="pm-segment-count">{opt.count}</span>
              </button>
            ))}
          </div>
          <button
            type="button"
            className="pm-icon-btn"
            onClick={load}
            disabled={loading}
            title="Refresh"
            aria-label="Refresh"
          >
            <Icon name="refresh" size={16} className={loading ? 'pm-spin' : undefined} />
          </button>
        </div>
      </div>

      {error && (
        <div
          style={{
            padding: '12px 16px',
            backgroundColor: '#fdecea',
            color: '#912018',
            border: '1px solid #f5c6c1',
            borderRadius: '8px',
            marginBottom: '16px',
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#6b716d' }}>
          Loading notifications…
        </div>
      ) : visibleFailures.length === 0 ? (
        <div
          style={{
            padding: '48px 24px',
            textAlign: 'center',
            color: '#6b716d',
            backgroundColor: '#ffffff',
            border: '1px solid #e3e7e4',
            borderRadius: '12px',
          }}
        >
          <div style={{ fontSize: '2rem', marginBottom: '8px' }}>✅</div>
          <div style={{ fontSize: '1.05rem', fontWeight: 600, color: '#1a1d1b' }}>
            {filter === 'processing'
              ? 'Nothing stuck after processing'
              : filter === 'ledger'
              ? 'Ledger writeback is healthy'
              : 'No failed payments'}
          </div>
          <div style={{ marginTop: '4px' }}>
            {filter === 'processing'
              ? 'A payment that showed Processing with no error and then never settled (voided/returned/failed) would show up here.'
              : filter === 'ledger'
              ? 'A payment that was collected but could not be posted to the PMS ledger (Dentrix) would show up here to retry.'
              : "Declines and payments that didn't complete will show up here as they happen."}
          </div>
        </div>
      ) : (
        <div
          style={{
            backgroundColor: '#ffffff',
            border: '1px solid #e3e7e4',
            borderRadius: '12px',
            overflow: 'hidden',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>When</th>
                <th style={thStyle}>Patient</th>
                <th style={thStyle}>Invoice</th>
                <th style={thStyle}>Card</th>
                <th style={thStyle}>Reason</th>
                <th style={thRight}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {visibleFailures.map((row) => (
                <tr key={row.key}>
                  <td style={tdStyle}>{formatDateTime(row.date)}</td>
                  <td style={tdStyle} data-phi>{row.patientName}</td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.82rem' }}>
                    {row.invoiceId}
                  </td>
                  <td style={tdStyle}>
                    {row.cardBrand || row.cardLast4
                      ? `${row.cardBrand || 'Card'} ${row.cardLast4 ? `•••• ${row.cardLast4}` : ''}`.trim()
                      : '—'}
                  </td>
                  <td style={tdStyle}>
                    <span style={{ color: '#b42318', fontWeight: 600 }}>
                      {row.reason}
                    </span>
                    {row.processingThenFailed && (
                      <span
                        title={`The invoice showed Processing with no error for ${formatGap(row.processingGapMs)} before it stopped, and it never settled (status: ${row.invoiceStatus}). Needs a retry.`}
                        style={{
                          display: 'inline-block',
                          marginLeft: '8px',
                          padding: '2px 8px',
                          borderRadius: '999px',
                          backgroundColor: '#fdecea',
                          color: '#912018',
                          border: '1px solid #f5c6c1',
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          verticalAlign: 'middle',
                        }}
                      >
                        ⏳→✗ Processing {formatGap(row.processingGapMs)} then stopped
                      </span>
                    )}
                    {row.ledgerFailed && (
                      <span
                        title={`The patient's payment was collected, but it could not be posted to the PMS ledger (invoice status: ${row.invoiceStatus}). Post it manually in the PMS or fix the cause and retry.`}
                        style={{
                          display: 'inline-block',
                          marginLeft: '8px',
                          padding: '2px 8px',
                          borderRadius: '999px',
                          backgroundColor: '#fdf3e3',
                          color: '#a8520c',
                          border: '1px solid #f3d9ae',
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          verticalAlign: 'middle',
                        }}
                      >
                        ⚠ PMS ledger not updated
                      </span>
                    )}
                  </td>
                  <td style={tdRight}>{usd(row.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default NotificationsTab;
